// The canned questions of the old Reports rail, kept as built-in reports: a pivot layout each,
// or a page of their own (Tickets, Bookings, Breaks, Wait estimates). View-only; Duplicate to edit.
import { api } from '../../lib/api'
import type { StaffKey, Vars } from '../../lib/staffText'
import { EMPTY_SPEC } from './layout'
import type { PivotSpec } from './types'

export interface BuiltIn {
  id: string
  name: StaffKey
  about: StaffKey
  /** A layout opened in the builder, or a page of its own. */
  spec?: PivotSpec
  to?: string
}

const spec = (s: Partial<PivotSpec>): PivotSpec => ({ ...EMPTY_SPEC, date: { preset: 'last_7' }, ...s })
const handling = ['served', 'handlingMedian', 'handlingMax']
const trend = ['tickets', 'waitMedian', 'serviceLevel']

export const BUILT_INS: BuiltIn[] = [
  { id: 'overview', name: 'pivot.bi.overview', about: 'pivot.bi.overviewAbout', spec: spec({ rows: ['service'], vals: ['tickets', 'served', 'waitMedian', 'serviceLevel'] }) },
  { id: 'branches', name: 'pivot.bi.branches', about: 'pivot.bi.branchesAbout', spec: spec({ rows: ['branch'], vals: ['tickets', 'served', 'waitMedian', 'serviceLevel'] }) },
  { id: 'hours', name: 'pivot.bi.hours', about: 'pivot.bi.hoursAbout', spec: spec({ rows: ['hour'], vals: ['tickets'] }) },
  { id: 'peak15', name: 'pivot.bi.peak15', about: 'pivot.bi.peak15About', spec: spec({ rows: ['interval15'], vals: ['tickets'] }) },
  { id: 'weekdays', name: 'pivot.bi.weekdays', about: 'pivot.bi.weekdaysAbout', spec: spec({ rows: ['weekday'], vals: ['tickets', 'serviceLevel'], date: { preset: 'last_30' } }) },
  { id: 'channels', name: 'pivot.bi.channels', about: 'pivot.bi.channelsAbout', spec: spec({ rows: ['channel', 'source'], vals: ['tickets'], date: { preset: 'last_30' } }) },
  { id: 'waits', name: 'pivot.bi.waits', about: 'pivot.bi.waitsAbout', spec: spec({ rows: ['waitBand'], vals: ['tickets'] }) },
  {
    id: 'compare',
    name: 'pivot.bi.compare',
    about: 'pivot.bi.compareAbout',
    spec: spec({ rows: ['service'], cols: ['date'], vals: ['tickets', 'serviceLevel'], dateGroup: 'month', date: { preset: 'this_and_last_month' } }),
  },
  { id: 'desks', name: 'pivot.bi.desks', about: 'pivot.bi.desksAbout', spec: spec({ rows: ['branch', 'desk'], vals: handling }) },
  { id: 'staff', name: 'pivot.bi.staff', about: 'pivot.bi.staffAbout', spec: spec({ rows: ['staff'], vals: handling }) },
  { id: 'breaks', name: 'pivot.bi.breaks', about: 'pivot.bi.breaksAbout', to: '/reports/breaks' },
  { id: 'weekly', name: 'pivot.bi.weekly', about: 'pivot.bi.weeklyAbout', spec: spec({ rows: ['date'], vals: trend, dateGroup: 'week', date: { preset: 'last_12_weeks' } }) },
  { id: 'monthly', name: 'pivot.bi.monthly', about: 'pivot.bi.monthlyAbout', spec: spec({ rows: ['date'], vals: trend, dateGroup: 'month', date: { preset: 'last_12_months' } }) },
  { id: 'tickets', name: 'pivot.bi.tickets', about: 'pivot.bi.ticketsAbout', to: '/reports/tickets' },
  { id: 'bookings', name: 'pivot.bi.bookings', about: 'pivot.bi.bookingsAbout', to: '/reports/bookings' },
  { id: 'estimates', name: 'pivot.bi.estimates', about: 'pivot.bi.estimatesAbout', to: '/reports/estimates' },
]

export const builtIn = (id: string) => BUILT_INS.find((b) => b.id === id)

/** Where a built-in opens. */
export const builtInPath = (b: BuiltIn) => b.to ?? `/reports/builtin/${b.id}`

type Say = (key: StaffKey, vars?: Vars) => string

/** A built-in's copy: "{name} copy", or "{name} copy n" for the first n not taken. */
export function copyName(name: string, taken: readonly string[], say: Say): string {
  for (let n = 1; ; n++) {
    const c = n === 1 ? say('pivot.copyName', { name }) : say('pivot.copyNameN', { name, n })
    if (!taken.includes(c)) return c
  }
}

/** copyName against the reports this person sees; without them, the first name. */
export async function nameForCopy(name: string, say: Say, organisation?: string): Promise<string> {
  try {
    const { views } = await api.reportViews(organisation)
    return copyName(name, views.map((v) => v.name), say)
  } catch {
    return copyName(name, [], say)
  }
}
